import { ListOrdered } from 'lucide-react';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router';
import { useError } from '@components/hooks/useError';
import { PageHeader } from '@components/page-header';
import { Spinner } from '@components/spinner';
import { Button } from '@components/ui/button';
import { links } from '@core/router';
import { toast } from '@core/toast';
import { injectTSDI } from '@core/tsdi';
import ChallengeFormSteps from '@src/communities/community/challenges/challenge-form/steps';
import { ChallengeActions } from './challange-actions';
import { ChallengeDataStore } from './data-store';
import { ChallengeStore } from './store';

export const ChallengeEditForm = observer(() => {
    const challengeDataStore = injectTSDI(ChallengeDataStore);
    const { liveChallengeConfiguration, challengeConfigurationRequest } =
        challengeDataStore;
    const { isUpdating, validateAllForms, updateChallenge, isModified } =
        injectTSDI(ChallengeStore);
    const { communityId, challengeId } = useParams();
    const navigate = useNavigate();
    const showError = useError();

    React.useEffect(() => {
        challengeDataStore.initializeWithParams(
            Number(challengeId),
            communityId ? Number(communityId) : undefined
        );
    }, [challengeDataStore]);

    const leaderboardEditable = liveChallengeConfiguration?.leaderboardEditable;

    return (
        <>
            <PageHeader
                title={liveChallengeConfiguration?.presentation.name.en ?? ''}
                actions={
                    leaderboardEditable && (
                        <Button
                            variant="outline"
                            onClick={() =>
                                navigate(
                                    links.communityChallengeLeaderboard(
                                        communityId.toString(),
                                        challengeId.toString()
                                    )
                                )
                            }
                        >
                            <ListOrdered />
                            Leaderboard
                        </Button>
                    )
                }
            />
            <div className="pb-24">
                {challengeConfigurationRequest.loading ? (
                    <div className="flex min-h-[200px] items-center justify-center">
                        <Spinner />
                    </div>
                ) : (
                    <ChallengeFormSteps />
                )}
            </div>
            <ChallengeActions
                communityId={communityId}
                isUpdating={isUpdating}
                showCreateButton={false}
                showCancelButton={false}
                onChallengeEdit={async () => {
                    const __hasError = await validateAllForms();

                    if (!__hasError) {
                        try {
                            await updateChallenge();
                            toast.success('Challenge was updated');
                            navigate(links.communityChallenges(communityId));
                        } catch (error) {
                            showError(error);
                        }
                    }
                }}
                editDisabled={isUpdating || !isModified}
                showEditButton={true}
            />
        </>
    );
});

export default ChallengeEditForm;
